import { t } from "@/lib/i18n";
import { useState } from "react";
import { BellRing, Loader2, MessageCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { caseWhatsAppUrl } from "@/lib/cases";

export const FollowUpDialog = ({ request, onClose, onCreate }) => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const close = () => {
    setCreated(null);
    setPhone("");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setCreated(await onCreate({ ...request, phone, name }));
      toast.success(t("fu.toast"));
    } catch (err) {
      toast.error(t("fu.err"));
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="followup-dialog" className="max-w-md rounded-2xl border-ac-line font-body">
        {!created ? (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <span className="mb-2 grid h-11 w-11 place-items-center rounded-xl bg-[#25D366]/15 text-[#128C4A]"><BellRing className="h-5 w-5" /></span>
              <DialogTitle className="font-display text-xl text-ac-ink">{t("fu.title")}</DialogTitle>
              <DialogDescription>{t("fu.desc")}</DialogDescription>
            </DialogHeader>
            <label className="block text-sm font-medium text-ac-text">
              {t("fu.name")}
              <input data-testid="followup-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-ac-line px-3 outline-none focus:border-ac-primary focus:ring-4 focus:ring-ac-primary-ring" />
            </label>
            <label className="block text-sm font-medium text-ac-text">
              {t("fu.phone")}
              <div className="mt-1 flex h-11 overflow-hidden rounded-xl border border-ac-line focus-within:border-ac-primary focus-within:ring-4 focus-within:ring-ac-primary-ring">
                <span className="grid place-items-center bg-ac-surface2 px-3 font-mono text-sm text-ac-muted">+52</span>
                <input data-testid="followup-phone-input" required inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="55 1234 5678" className="flex-1 px-3 font-mono outline-none" />
              </div>
            </label>
            <p className="flex gap-2 text-xs text-ac-muted"><ShieldCheck className="h-4 w-4 shrink-0 text-ac-primary" /> {t("fu.privacy")}</p>
            <button data-testid="followup-submit-button" disabled={busy || phone.length !== 10} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-ac-strong font-semibold text-white transition-colors hover:bg-ac-strong-hover disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} {t("fu.submit")}
            </button>
          </form>
        ) : (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-ac-primary" />
            <DialogTitle className="font-display text-xl text-ac-ink">{t("fu.done")}</DialogTitle>
            <div className="rounded-xl border border-dashed border-ac-accent bg-ac-accent-soft py-3">
              <p className="text-xs uppercase tracking-widest text-ac-accent-ink">{t("fu.folio")}</p>
              <p data-testid="followup-folio" className="font-mono text-2xl font-bold text-ac-ink">{created.folio}</p>
            </div>
            <a data-testid="followup-whatsapp-link" href={caseWhatsAppUrl(created, "created")} target="_blank" rel="noopener noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#25D366] font-semibold text-white hover:brightness-95">
              <MessageCircle className="h-5 w-5" /> {t("fu.saveWa")}
            </a>
            <p className="text-xs text-ac-muted">{t("fu.find")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
